/**
 * MyPage Component
 * 앱웹 전용 마이페이지 - VROONG 브랜드 통일 디자인
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BottomNavigation from '../../../../core/components/BottomNavigation';
import { useMyPage } from '../hooks/useMyPage';
import { useDeferredLoading } from '../../../../core/hooks/useDeferredLoading';
import AccountDeletionModal from './AccountDeletionModal';
import { requestAccountDeletion, getRemainingDeposit, checkWeeklyPerformance, checkDailyDeductions } from "../api/myPageApi";
import { commonCodeApi } from '../../../../core/api/commonCodeApi';
import { CommonCodeDto } from '../../../../core/types/commonCode.types';
import { getUserInfo } from '../../../../core/utils';
import styles from '../styles/MyPage.module.css';

const MyPage: React.FC = () => {
    const navigate = useNavigate();
    const dateInputRef = useRef<HTMLInputElement>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [remainingDeposit, setRemainingDeposit] = useState<number>(0);
    const [isLoadingDeposit, setIsLoadingDeposit] = useState(false);
    const [hasWeeklyPerformance, setHasWeeklyPerformance] = useState<boolean>(false);
    const [hasDailyDeductions, setHasDailyDeductions] = useState<boolean>(false);
    const [bankCodes, setBankCodes] = useState<CommonCodeDto[]>([]);

    // 날짜 포맷 함수 ("20251231" → "2025-12-31")
    const formatDateDisplay = (dateStr: string): string => {
        if (!dateStr || dateStr.length !== 8) return dateStr;
        const year = dateStr.substring(0, 4);
        const month = dateStr.substring(4, 6);
        const day = dateStr.substring(6, 8);
        return `${year}-${month}-${day}`;
    };

    // 사용자 정보 먼저 확인 (API 호출 전)
    const userInfo = getUserInfo();
    const isAdmin = userInfo?.prmCode === 'A';

    // useMyPage hook 호출은 무조건 해야 함 (React hooks 규칙)
    const {
        myPageInfo,
        accountForm,
        depositAccountForm,
        acctApplyType,
        acctApplyTypeName,
        selectedNextSettlementType,
        preAcctApplyDate,
        nextAcctApplyType,
        nextAcctApplyTypeName,
        nextAcctApplyDate,
        isLoading: rawIsLoading,
        isUpdating,
        error,
        updateAccountForm,
        updateDepositAccountForm,
        updateAcctApplyType,
        updateNextAcctApplyDate,
        updateSettlement,
        refreshData,
        setPreAcctApplyDate,
        formatTimestampArray,
        formatBirthDate,
        formatRegistNum,
        formatPhoneNumber,
        getNextWednesday,
        isDataLoaded,
    } = useMyPage();

    // 지연 로딩 적용 (200ms 이상 로딩 시에만 로딩 UI 표시)
    const isLoading = useDeferredLoading(rawIsLoading, 200);

    const handleBackClick = () => {
        navigate(-1);
    };

    // 은행 코드 목록 조회
    useEffect(() => {
        const fetchBankCodes = async () => {
            try {
                const response = await commonCodeApi.getCommonCodeList('BANK_CODE');
                if (response.codes && response.codes.length > 0) {
                    const activeBanks = response.codes.filter(code => code.useYn === 'Y');
                    activeBanks.sort((a, b) => a.comSeqno - b.comSeqno);
                    setBankCodes(activeBanks);
                }
            } catch (error) {
                console.error('은행 코드 조회 실패:', error);
            }
        };

        fetchBankCodes();
    }, []);

    // 날짜 아이콘 클릭 핸들러
    const handleDateIconClick = () => {
        if (dateInputRef.current) {
            dateInputRef.current.showPicker?.();
        }
    };

    const handleOpenModal = async () => {
        setIsLoadingDeposit(true);
        try {
            const [depositResponse, performanceResponse, deductionResponse] = await Promise.all([
                getRemainingDeposit(),
                checkWeeklyPerformance(),
                checkDailyDeductions()
            ]);

            if (depositResponse.success) {
                setRemainingDeposit(depositResponse.remainingDeposit);
            } else {
                setRemainingDeposit(0);
            }

            if (performanceResponse.success) {
                setHasWeeklyPerformance(performanceResponse.hasWeeklyPerformance);
            } else {
                setHasWeeklyPerformance(false);
            }

            if (deductionResponse.success) {
                setHasDailyDeductions(deductionResponse.hasDailyDeductions);
            } else {
                setHasDailyDeductions(false);
            }
        } catch (error) {
            console.error('탈퇴 정보 조회 실패:', error);
            setRemainingDeposit(0);
            setHasWeeklyPerformance(false);
            setHasDailyDeductions(false);
        } finally {
            setIsLoadingDeposit(false);
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handleLogout = () => {
        if (window.confirm('로그아웃 하시겠습니까?')) {
            localStorage.clear();
            sessionStorage.clear();

            document.cookie.split(";").forEach(cookie => {
                const cookieName = cookie.split("=")[0].trim();
                document.cookie = `${cookieName}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
                document.cookie = `${cookieName}=; path=/; domain=${window.location.hostname}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
            });

            window.location.href = '/login';
        }
    };

    const handleConfirmDeletion = async () => {
        // 잔여 예치금이 음수인 경우 탈퇴 불가
        if (remainingDeposit < 0) {
            alert('잔여 예치금이 마이너스입니다. 정산을 완료한 후 탈퇴 요청해주세요.');
            return;
        }

        // 주정산 대상 수행내역이 있는 경우 탈퇴 불가
        if (hasWeeklyPerformance) {
            alert('주정산 대상 수행내역이 있습니다. 정산이 완료된 후 탈퇴 요청해주세요.');
            return;
        }

        // 잔여 일차감 내역이 있는 경우 탈퇴 불가
        if (hasDailyDeductions) {
            alert('잔여 일차감 내역이 있습니다. 일차감이 완료된 후 탈퇴 요청해주세요.');
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await requestAccountDeletion({ confirmed: true });

            if (response.success) {
                alert(response.message);
                setIsModalOpen(false);
            } else {
                alert(response.message || '탈퇴 요청에 실패했습니다.');
            }
        } catch (error) {
            console.error('탈퇴 요청 실패: ', error);
            alert('탈퇴 요청에 실패했습니다. 다시 시도해주세요.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // 관리자는 로딩/에러 체크 없이 바로 화면 렌더링
    if (isAdmin) {
        return (
            <div className={`screen active ${styles['mypage-screen']}`}>
                <div className={styles['mypage-header']}>
                    <div className={styles['header-top']}>
                        <button className={styles['back-btn']} onClick={handleBackClick}>
                            <i className="fas fa-arrow-left"></i>
                        </button>
                        <h1 className={styles['page-title']}>마이페이지</h1>
                    </div>
                </div>
                <div className={styles['mypage-container']}>
                    {/* 프로필 카드 */}
                    <div className={styles['profile-card']}>
                        <div className={styles['profile-header']}>
                            <div className={styles['profile-icon']}>👑</div>
                            <div className={styles['profile-info']}>
                                <h2 className={styles['profile-name']}>관리자</h2>
                                <span className={styles['profile-id']}>{userInfo?.suppId || userInfo?.riderId || localStorage.getItem('userId')}</span>
                            </div>
                        </div>
                        <div className={styles['profile-status']}>
                            <span>✅</span>
                            관리자
                        </div>
                    </div>

                    {/* 관리자 안내 */}
                    <div className={styles['info-card']}>
                        <h3 className={styles['section-title']}>
                            <span className={styles['section-title-icon']}>ℹ️</span>
                            안내
                        </h3>
                        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                            <p>관리자 계정입니다.</p>
                            <p style={{ marginTop: '10px' }}>협력사 또는 라이더를 선택하여</p>
                            <p>해당 계정의 정보를 확인할 수 있습니다.</p>
                        </div>
                    </div>

                    {/* 로그아웃 버튼 */}
                    <div className={styles['button-group']}>
                        <button
                            className={`${styles['btn']} ${styles['btn-logout']}`}
                            onClick={handleLogout}
                        >
                            로그아웃
                        </button>
                    </div>
                </div>
                <BottomNavigation currentScreen="my-page" />
            </div>
        );
    }

    // 로딩 상태 (관리자 아닌 경우만)
    if (isLoading) {
        return (
            <div className={`screen active ${styles['mypage-screen']}`}>
                <div className={styles['mypage-header']}>
                    <div className={styles['header-top']}>
                        <button className={styles['back-btn']} onClick={handleBackClick}>
                            <i className="fas fa-arrow-left"></i>
                        </button>
                        <h1 className={styles['page-title']}>마이페이지</h1>
                    </div>
                </div>
                <div className={styles['mypage-container']}>
                    <div className={styles['loading-section']}>
                        <div className={styles['loading-spinner']}></div>
                        <p className={styles['loading-text']}>정보를 불러오는 중...</p>
                    </div>
                </div>
                <BottomNavigation currentScreen="my-page" />
            </div>
        );
    }

    // 에러 상태
    if (error) {
        return (
            <div className={`screen active ${styles['mypage-screen']}`}>
                <div className={styles['mypage-header']}>
                    <div className={styles['header-top']}>
                        <button className={styles['back-btn']} onClick={handleBackClick}>
                            <i className="fas fa-arrow-left"></i>
                        </button>
                        <h1 className={styles['page-title']}>마이페이지</h1>
                    </div>
                </div>
                <div className={styles['mypage-container']}>
                    <div className={styles['error-section']}>
                        <div className={styles['error-icon']}>⚠️</div>
                        <p className={styles['error-text']}>정보를 불러오는데 실패했습니다.</p>
                        <button className={styles['retry-btn']} onClick={refreshData}>
                            다시 시도
                        </button>
                    </div>
                </div>
                <BottomNavigation currentScreen="my-page" />
            </div>
        );
    }

    const isSupplier = !!userInfo?.suppId && !userInfo?.riderId;

    // 협력사 페이지
    if (isSupplier && myPageInfo) {
        return (
            <div className={`screen active ${styles['mypage-screen']}`}>
                <div className={styles['mypage-header']}>
                    <div className={styles['header-top']}>
                        <button className={styles['back-btn']} onClick={handleBackClick}>
                            <i className="fas fa-arrow-left"></i>
                        </button>
                        <h1 className={styles['page-title']}>마이페이지</h1>
                    </div>
                </div>
                <div className={styles['mypage-container']}>
                    {/* 프로필 카드 */}
                    <div className={styles['profile-card']}>
                        <div className={styles['profile-header']}>
                            <div className={styles['profile-icon']}>
                                <i className="fas fa-building"></i>
                            </div>
                            <div className={styles['profile-info']}>
                                <h2 className={styles['profile-name']}>{myPageInfo.riderName || '-'}</h2>
                                <span className={styles['profile-id']}>{myPageInfo.suppId || myPageInfo.riderId}</span>
                            </div>
                        </div>
                        <div className={styles['profile-status']}>
                            <i className="fas fa-check-circle"></i>
                            협력사
                        </div>
                    </div>

                    {/* 협력사 정보 */}
                    <div className={styles['info-card']}>
                        <h3 className={styles['section-title']}>
                            <i className={`fas fa-info-circle ${styles['section-title-icon']}`}></i>
                            협력사 정보
                        </h3>
                        <div className={styles['info-list']}>
                            <div className={styles['info-item']}>
                                <div className={styles['info-item-left']}>
                                    <span className={styles['info-icon']}><i className="fas fa-id-card"></i></span>
                                    <span className={styles['info-label']}>협력사 ID</span>
                                </div>
                                <span className={styles['info-value']}>{myPageInfo.suppId || myPageInfo.riderId}</span>
                            </div>
                            <div className={styles['info-item']}>
                                <div className={styles['info-item-left']}>
                                    <span className={styles['info-icon']}><i className="fas fa-building"></i></span>
                                    <span className={styles['info-label']}>협력사명</span>
                                </div>
                                <span className={styles['info-value']}>{myPageInfo.riderName || '-'}</span>
                            </div>
                            <div className={styles['info-item']}>
                                <div className={styles['info-item-left']}>
                                    <span className={styles['info-icon']}><i className="fas fa-file-alt"></i></span>
                                    <span className={styles['info-label']}>사업자번호</span>
                                </div>
                                <span className={styles['info-value']}>{myPageInfo.registNum || '-'}</span>
                            </div>
                            <div className={styles['info-item']}>
                                <div className={styles['info-item-left']}>
                                    <span className={styles['info-icon']}><i className="fas fa-store"></i></span>
                                    <span className={styles['info-label']}>사업자상호</span>
                                </div>
                                <span className={styles['info-value']}>{myPageInfo.suppCompName || '-'}</span>
                            </div>
                            <div className={styles['info-item']}>
                                <div className={styles['info-item-left']}>
                                    <span className={styles['info-icon']}><i className="fas fa-map-marker-alt"></i></span>
                                    <span className={styles['info-label']}>배달권역</span>
                                </div>
                                <span className={styles['info-value']}>{myPageInfo.deliveryArea || '-'}</span>
                            </div>
                            <div className={styles['info-item']}>
                                <div className={styles['info-item-left']}>
                                    <span className={styles['info-icon']}><i className="fas fa-calendar-check"></i></span>
                                    <span className={styles['info-label']}>등록일</span>
                                </div>
                                <span className={styles['info-value']}>{formatTimestampArray(myPageInfo.riderInsDate)}</span>
                            </div>
                        </div>
                    </div>

                    {/* 계좌 정보 (출금 + 입금 통합) */}
                    <div className={styles['info-card']}>
                        <h3 className={styles['section-title']}>
                            <i className={`fas fa-university ${styles['section-title-icon']}`}></i>
                            계좌 정보
                        </h3>

                        {/* 출금 계좌 */}
                        <div className={styles['account-subsection-header']}>
                            <span className={styles['account-subsection-title']}>출금 계좌</span>
                            <span className={`${styles['account-badge']} ${styles['account-badge--edit']}`}>
                                <i className="fas fa-pen"></i>
                                수정 가능
                            </span>
                        </div>
                        <div className={styles['input-group']}>
                            <label className={styles['input-label']}>은행</label>
                            <select
                                value={accountForm.bankCode || ''}
                                onChange={(e) => updateAccountForm('bankCode', e.target.value)}
                                className={styles['form-select']}
                            >
                                <option value="">은행 선택</option>
                                {bankCodes.map((bank: CommonCodeDto) => (
                                    <option key={bank.comCode} value={bank.comCode}>
                                        {bank.comCdName}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className={styles['input-group']}>
                            <label className={styles['input-label']}>계좌번호</label>
                            <input
                                type="text"
                                value={accountForm.accountNum}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/[^0-9]/g, '');
                                    updateAccountForm('accountNum', value);
                                }}
                                className={styles['form-input']}
                            />
                        </div>
                        <div className={styles['input-group']}>
                            <label className={styles['input-label']}>예금주</label>
                            <input
                                type="text"
                                value={accountForm.accName}
                                onChange={(e) => updateAccountForm('accName', e.target.value.replace(/[^a-zA-Zㄱ-ㅎ가-힣\s]/g, ''))}
                                className={styles['form-input']}
                            />
                        </div>

                        <div className={styles['account-divider']} />

                        {/* 입금 계좌 */}
                        <div className={styles['account-subsection-header']}>
                            <span className={styles['account-subsection-title']}>입금 계좌</span>
                            <span className={`${styles['account-badge']} ${styles['account-badge--readonly']}`}>
                                <i className="fas fa-lock"></i>
                                조회 전용
                            </span>
                        </div>
                        <div className={styles['input-group']}>
                            <label className={styles['input-label']}>은행</label>
                            <select
                                value={depositAccountForm.dpBankCode || ''}
                                onChange={(e) => updateDepositAccountForm('dpBankCode', e.target.value)}
                                className={styles['form-select']}
                                disabled
                            >
                                <option value="">은행 선택</option>
                                {bankCodes.map((bank: CommonCodeDto) => (
                                    <option key={bank.comCode} value={bank.comCode}>
                                        {bank.comCdName}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className={styles['input-group']}>
                            <label className={styles['input-label']}>계좌번호</label>
                            <input
                                type="text"
                                value={depositAccountForm.dpAcctNum}
                                onChange={(e) => updateDepositAccountForm('dpAcctNum', e.target.value)}
                                className={styles['form-input']}
                                disabled
                            />
                        </div>
                        <div className={styles['input-group']}>
                            <label className={styles['input-label']}>예금주</label>
                            <input
                                type="text"
                                value={depositAccountForm.dpAcctName}
                                onChange={(e) => updateDepositAccountForm('dpAcctName', e.target.value)}
                                className={styles['form-input']}
                                disabled
                            />
                        </div>
                    </div>

                    {/* 버튼 그룹 */}
                    <div className={styles['button-group']}>
                        <button
                            className={`${styles['btn']} ${styles['btn-primary']}`}
                            onClick={updateSettlement}
                            disabled={isUpdating}
                        >
                            {isUpdating ? '저장 중...' : '저장'}
                        </button>
                        <button
                            className={styles['btn-logout-text']}
                            onClick={handleLogout}
                        >
                            로그아웃
                        </button>
                    </div>
                </div>
                <BottomNavigation currentScreen="my-page" />
            </div>
        );
    }

    // 라이더 페이지
    return (
        <div className={`screen active ${styles['mypage-screen']}`}>
            <div className={styles['mypage-header']}>
                <div className={styles['header-top']}>
                    <button className={styles['back-btn']} onClick={handleBackClick}>
                        <i className="fas fa-arrow-left"></i>
                    </button>
                    <h1 className={styles['page-title']}>마이페이지</h1>
                </div>
            </div>
            <div className={styles['mypage-container']}>
                {isDataLoaded && myPageInfo && (
                    <>
                        {/* 프로필 카드 */}
                        <div className={styles['profile-card']}>
                            <div className={styles['profile-header']}>
                                <div className={styles['profile-icon']}>
                                    <i className="fas fa-motorcycle"></i>
                                </div>
                                <div className={styles['profile-info']}>
                                    <h2 className={styles['profile-name']}>{myPageInfo.riderName}</h2>
                                    <span className={styles['profile-id']}>{myPageInfo.riderId}</span>
                                </div>
                            </div>
                            <div className={styles['profile-status']}>
                                <i className="fas fa-check-circle"></i>
                                {myPageInfo.contStatusName}
                            </div>
                        </div>

                        {/* 기본 정보 */}
                        <div className={styles['info-card']}>
                            <h3 className={styles['section-title']}>
                                <i className={`fas fa-user ${styles['section-title-icon']}`}></i>
                                기본 정보
                            </h3>
                            <div className={styles['info-list']}>
                                <div className={styles['info-item']}>
                                    <div className={styles['info-item-left']}>
                                        <span className={styles['info-icon']}><i className="fas fa-phone"></i></span>
                                        <span className={styles['info-label']}>휴대폰 번호</span>
                                    </div>
                                    <span className={styles['info-value']}>{formatPhoneNumber(myPageInfo.phoneNum)}</span>
                                </div>
                                <div className={styles['info-item']}>
                                    <div className={styles['info-item-left']}>
                                        <span className={styles['info-icon']}><i className="fas fa-birthday-cake"></i></span>
                                        <span className={styles['info-label']}>생년월일</span>
                                    </div>
                                    <span className={styles['info-value']}>{formatBirthDate(myPageInfo.birthDate)}</span>
                                </div>
                                <div className={styles['info-item']}>
                                    <div className={styles['info-item-left']}>
                                        <span className={styles['info-icon']}><i className="fas fa-id-card"></i></span>
                                        <span className={styles['info-label']}>주민등록번호</span>
                                    </div>
                                    <span className={styles['info-value']}>{formatRegistNum(myPageInfo.registNum)}</span>
                                </div>
                                <div className={styles['info-item']}>
                                    <div className={styles['info-item-left']}>
                                        <span className={styles['info-icon']}><i className="fas fa-motorcycle"></i></span>
                                        <span className={styles['info-label']}>배달수단</span>
                                    </div>
                                    <span className={styles['info-value']}>{myPageInfo.deliveryMethName}</span>
                                </div>
                                <div className={styles['info-item']}>
                                    <div className={styles['info-item-left']}>
                                        <span className={styles['info-icon']}><i className="fas fa-won-sign"></i></span>
                                        <span className={styles['info-label']}>정산여부</span>
                                    </div>
                                    <span className={`${styles['info-value']} ${styles['highlight']}`}>
                                        {acctApplyTypeName || '선정산'} ({myPageInfo?.acctApplyDay ? formatDateDisplay(myPageInfo.acctApplyDay) : preAcctApplyDate})
                                    </span>
                                </div>
                                <div className={styles['info-item']}>
                                    <div className={styles['info-item-left']}>
                                        <span className={styles['info-icon']}><i className="fas fa-exchange-alt"></i></span>
                                        <span className={styles['info-label']}>정산변경예정</span>
                                    </div>
                                    <span className={`${styles['info-value']} ${styles['highlight']}`}>
                                        {myPageInfo?.nextAcctApplyDay
                                            ? `${nextAcctApplyTypeName || '선정산'} (${formatDateDisplay(myPageInfo.nextAcctApplyDay)})`
                                            : '변경 예정 없음'
                                        }
                                    </span>
                                </div>
                                <div className={styles['info-item']}>
                                    <div className={styles['info-item-left']}>
                                        <span className={styles['info-icon']}><i className="fas fa-calendar-check"></i></span>
                                        <span className={styles['info-label']}>등록일</span>
                                    </div>
                                    <span className={styles['info-value']}>{formatTimestampArray(myPageInfo.riderInsDate)}</span>
                                </div>
                                <div className={styles['info-item']}>
                                    <div className={styles['info-item-left']}>
                                        <span className={styles['info-icon']}><i className="fas fa-calendar-times"></i></span>
                                        <span className={styles['info-label']}>계약종료일</span>
                                    </div>
                                    <span className={styles['info-value']}>{formatTimestampArray(myPageInfo.contEdDate)}</span>
                                </div>
                            </div>
                        </div>

                        {/* 계좌 정보 (출금 + 입금 통합) */}
                        <div className={styles['info-card']}>
                            <h3 className={styles['section-title']}>
                                <i className={`fas fa-university ${styles['section-title-icon']}`}></i>
                                계좌 정보
                            </h3>

                            {/* 출금 계좌 */}
                            <div className={styles['account-subsection-header']}>
                                <span className={styles['account-subsection-title']}>출금 계좌</span>
                                <span className={`${styles['account-badge']} ${styles['account-badge--edit']}`}>
                                    <i className="fas fa-pen"></i>
                                    수정 가능
                                </span>
                            </div>
                            <div className={styles['input-group']}>
                                <label className={styles['input-label']}>은행</label>
                                <select
                                    value={accountForm.bankCode || ''}
                                    onChange={(e) => updateAccountForm('bankCode', e.target.value)}
                                    className={styles['form-select']}
                                >
                                    <option value="">은행 선택</option>
                                    {bankCodes.map((bank: CommonCodeDto) => (
                                        <option key={bank.comCode} value={bank.comCode}>
                                            {bank.comCdName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className={styles['input-group']}>
                                <label className={styles['input-label']}>계좌번호</label>
                                <input
                                    type="text"
                                    value={accountForm.accountNum}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/[^0-9]/g, '');
                                        updateAccountForm('accountNum', value);
                                    }}
                                    className={styles['form-input']}
                                />
                            </div>
                            <div className={styles['input-group']}>
                                <label className={styles['input-label']}>예금주</label>
                                <input
                                    type="text"
                                    value={accountForm.accName}
                                    onChange={(e) => updateAccountForm('accName', e.target.value.replace(/[^a-zA-Zㄱ-ㅎ가-힣\s]/g, ''))}
                                    className={styles['form-input']}
                                />
                            </div>

                            <div className={styles['account-divider']} />

                            {/* 입금 계좌 */}
                            <div className={styles['account-subsection-header']}>
                                <span className={styles['account-subsection-title']}>입금 계좌</span>
                                <span className={`${styles['account-badge']} ${styles['account-badge--readonly']}`}>
                                    <i className="fas fa-lock"></i>
                                    조회 전용
                                </span>
                            </div>
                            <div className={styles['input-group']}>
                                <label className={styles['input-label']}>은행</label>
                                <select
                                    value={depositAccountForm.dpBankCode || ''}
                                    onChange={(e) => updateDepositAccountForm('dpBankCode', e.target.value)}
                                    className={styles['form-select']}
                                    disabled
                                >
                                    <option value="">은행 선택</option>
                                    {bankCodes.map((bank: CommonCodeDto) => (
                                        <option key={bank.comCode} value={bank.comCode}>
                                            {bank.comCdName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className={styles['input-group']}>
                                <label className={styles['input-label']}>계좌번호</label>
                                <input
                                    type="text"
                                    value={depositAccountForm.dpAcctNum}
                                    onChange={(e) => updateDepositAccountForm('dpAcctNum', e.target.value)}
                                    className={styles['form-input']}
                                    disabled
                                />
                            </div>
                            <div className={styles['input-group']}>
                                <label className={styles['input-label']}>예금주</label>
                                <input
                                    type="text"
                                    value={depositAccountForm.dpAcctName}
                                    onChange={(e) => updateDepositAccountForm('dpAcctName', e.target.value)}
                                    className={styles['form-input']}
                                    disabled
                                />
                            </div>
                        </div>

                        {/* 정산 설정 */}
                        <div className={styles['info-card']}>
                            <h3 className={styles['section-title']}>
                                <i className={`fas fa-cog ${styles['section-title-icon']}`}></i>
                                정산 설정
                            </h3>
                            <div className={styles['settlement-options']}>
                                <div className={styles['radio-group']}>
                                    <label className={`${styles['radio-option']} ${selectedNextSettlementType === 'W' ? styles['selected'] : ''}`}>
                                        <input
                                            type="radio"
                                            name="settlementType"
                                            value="W"
                                            checked={selectedNextSettlementType === 'W'}
                                            onChange={() => updateAcctApplyType('W')}
                                        />
                                        <i className={`fas fa-check ${styles['radio-check']}`}></i>
                                        <span className={styles['radio-label']}>주정산</span>
                                    </label>
                                    <label className={`${styles['radio-option']} ${selectedNextSettlementType === 'P' ? styles['selected'] : ''}`}>
                                        <input
                                            type="radio"
                                            name="settlementType"
                                            value="P"
                                            checked={selectedNextSettlementType === 'P'}
                                            onChange={() => updateAcctApplyType('P')}
                                        />
                                        <i className={`fas fa-check ${styles['radio-check']}`}></i>
                                        <span className={styles['radio-label']}>선정산</span>
                                    </label>
                                </div>
                                <div className={styles['apply-date-group']}>
                                    <span className={styles['apply-date-label']}>적용일자</span>
                                    <div className={styles['date-input-box']}>
                                        <input
                                            ref={dateInputRef}
                                            type="date"
                                            className={styles['date-input']}
                                            value={nextAcctApplyDate || preAcctApplyDate || getNextWednesday()}
                                            onChange={(e) => {
                                                const selectedDate = new Date(e.target.value);
                                                const dayOfWeek = selectedDate.getDay();

                                                if (dayOfWeek !== 3) {
                                                    alert('수요일만 선택 가능합니다.');
                                                    const currentDate = nextAcctApplyDate || preAcctApplyDate || getNextWednesday();
                                                    if (dateInputRef.current) {
                                                        dateInputRef.current.value = currentDate;
                                                    }
                                                    return;
                                                }

                                                updateNextAcctApplyDate(e.target.value);
                                            }}
                                            onKeyDown={(e) => e.preventDefault()}
                                            min={new Date().toISOString().split('T')[0]}
                                            title="수요일만 선택 가능합니다"
                                        />
                                        <i className={`fas fa-calendar-alt ${styles['date-icon']}`} onClick={handleDateIconClick}></i>
                                    </div>
                                </div>
                                <p className={styles['settlement-hint']}>
                                    <i className="fas fa-info-circle"></i>
                                    주정산 선택 시 적용일자는 수요일만 선택 가능합니다
                                </p>
                            </div>
                        </div>

                        {/* 버튼 그룹 */}
                        <div className={styles['button-group']}>
                            <button
                                className={`${styles['btn']} ${styles['btn-primary']}`}
                                onClick={updateSettlement}
                                disabled={isUpdating}
                            >
                                {isUpdating ? '저장 중...' : '저장'}
                            </button>
                            <button
                                className={styles['btn-logout-text']}
                                onClick={handleLogout}
                                disabled={isUpdating}
                            >
                                로그아웃
                            </button>
                            <button
                                className={styles['btn-withdrawal-text']}
                                onClick={handleOpenModal}
                                disabled={isUpdating}
                            >
                                탈퇴요청
                            </button>
                        </div>
                    </>
                )}
            </div>

            <AccountDeletionModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onConfirm={handleConfirmDeletion}
                isSubmitting={isSubmitting || isLoadingDeposit}
                remainingDeposit={remainingDeposit}
                hasDailyDeductions={hasDailyDeductions}
                hasWeeklyPerformance={hasWeeklyPerformance}
            />

            <BottomNavigation currentScreen="my-page" />
        </div>
    );
};

export default MyPage;
